import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authService } from "../../services";
import { useBranding } from "../../context/BrandingContext";

export default function ResetPasswordPage() {
  const { businessName, tagline, effectiveLogo } = useBranding();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (!otp.trim() || otp.trim().length < 4) {
      setError("Please enter the 6-digit OTP code.");
      return;
    }
    if (!newPassword) {
      setError("Please enter your new password.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await authService.resetPassword(email.trim(), otp.trim(), newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center flex flex-col items-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-white border border-background-200 p-1.5 shadow-sm overflow-hidden">
            <img src={effectiveLogo} alt={businessName} className="h-full w-full object-contain" />
          </span>
          <h1 className="mt-4 text-xl font-semibold text-foreground-950">{businessName}</h1>
          <p className="mt-0.5 text-xs text-foreground-500">{tagline}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-6 shadow-sm sm:p-8">
          {success ? (
            <div className="py-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-100 text-success-600 shadow-sm">
                <i className="ri-checkbox-circle-fill text-3xl" />
              </span>
              <h2 className="mt-4 text-lg font-bold text-foreground-900">Password Changed Successfully!</h2>
              <p className="mt-2 text-sm text-foreground-600">
                Your password has been updated. You can now sign in with your new credentials.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white shadow hover:bg-primary-700 transition-colors"
              >
                <i className="ri-login-box-line text-base" /> Proceed to Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-foreground-900">Reset Your Password</h2>
                <p className="mt-1 text-xs text-foreground-500">
                  Enter your email, the OTP sent to your inbox, and your new password.
                </p>
              </div>

              {error && (
                <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 px-3.5 py-3 text-sm text-danger-700">
                  <i className="ri-close-circle-line mt-0.5 flex-none text-base" />
                  <span className="leading-snug">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs font-semibold text-foreground-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="h-10 w-full rounded-lg border border-background-300 bg-white px-3 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="otp" className="block text-xs font-semibold text-foreground-700">
                    6-Digit OTP Code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 6-digit OTP"
                    className="h-11 w-full text-center tracking-[0.3em] font-mono text-lg font-bold rounded-lg border border-background-300 bg-white px-3 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="newPassword" className="block text-xs font-semibold text-foreground-700">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="h-10 w-full rounded-lg border border-background-300 bg-white pl-3 pr-10 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-400 hover:text-foreground-600"
                    >
                      <i className={showNewPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="confirmPassword" className="block text-xs font-semibold text-foreground-700">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className="h-10 w-full rounded-lg border border-background-300 bg-white pl-3 pr-10 text-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-400 hover:text-foreground-600"
                    >
                      <i className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 text-sm font-medium text-white shadow transition-all hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 disabled:cursor-not-allowed disabled:bg-primary-300"
                >
                  {submitting ? <i className="ri-loader-4-line animate-spin text-base" /> : <i className="ri-check-line text-base" />}
                  {submitting ? "Resetting Password…" : "Reset Password"}
                </button>
              </form>

              <p className="mt-6 text-center text-sm">
                <Link to="/forgot-password" className="text-xs font-medium text-primary-600 hover:underline">
                  Need a new OTP? Go to Forgot Password
                </Link>
              </p>

              <p className="mt-2 text-center text-sm">
                <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-foreground-600 hover:text-foreground-800 hover:underline">
                  <i className="ri-arrow-left-line" /> Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
